import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import { isYoutubeUrl } from '../utils/youtube';
import { YOUTUBE_CHANNEL_CATEGORY, YOUTUBE_CHANNEL_SORT } from '../utils/useYoutubeChannel';

// Admin section (top of "유튜브 관리") — register the channel URL and toggle its
// visibility on the home page + footer. No new backend: reuses /images API,
// storing imageUrl = URL and alt = 'on' | 'off'.
export default function YoutubeChannelManager() {
  const { getIdToken } = useAuth();
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const fetchChannel = useCallback(async () => {
    try {
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images?category=${YOUTUBE_CHANNEL_CATEGORY}`);
      const data = await res.json();
      const item = (data.images || [])[0];
      if (item) {
        setUrl(item.imageUrl || '');
        setEnabled(item.alt === 'on');
      }
    } catch {
      setError('채널 설정을 불러오지 못했습니다.');
    }
  }, []);

  useEffect(() => { fetchChannel(); }, [fetchChannel]);

  async function handleSave(nextEnabled) {
    const trimmed = url.trim();
    if (trimmed && !isYoutubeUrl(trimmed)) {
      setError('올바른 유튜브 URL이 아닙니다 (youtube.com / youtu.be).');
      return;
    }
    if (nextEnabled && !trimmed) {
      setError('채널 URL을 먼저 입력해주세요.');
      return;
    }
    setError(''); setSaved(false); setBusy(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('로그인 세션이 만료되었습니다.');
      const res = await fetch(`${AWS_CONFIG.apiUrl}/images/${YOUTUBE_CHANNEL_CATEGORY}/${YOUTUBE_CHANNEL_SORT}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ imageUrl: trimmed, alt: nextEnabled ? 'on' : 'off' }),
      });
      if (!res.ok) throw new Error(`저장 실패 ${res.status}`);
      setEnabled(nextEnabled);
      setSaved(true);
    } catch (err) {
      setError(`저장 실패: ${err.message}`);
    }
    setBusy(false);
  }

  return (
    <div className="card" style={{ padding: 16, marginBottom: 24 }}>
      <h2 style={{ fontSize: '1.1rem', margin: '0 0 4px' }}>유튜브 채널 링크</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 0, marginBottom: 12 }}>
        채널 URL을 등록하고 on/off로 노출을 제어합니다. <strong style={{ color: 'var(--text)' }}>
        현재 {enabled ? '노출 중(ON)' : '숨김(OFF)'}</strong> — OFF면 홈·푸터 어디에도 보이지 않습니다.
      </p>

      {error && (
        <div style={{ color: '#ff5252', fontSize: '0.85rem', marginBottom: 10 }}>{error}</div>
      )}
      {saved && !error && (
        <div style={{ color: 'var(--primary)', fontSize: '0.85rem', marginBottom: 10 }}>저장되었습니다.</div>
      )}

      <input
        className="input"
        placeholder="채널 URL (예: https://www.youtube.com/@koreaonauto)"
        value={url}
        onChange={(e) => { setUrl(e.target.value); setSaved(false); }}
        style={{ width: '100%', marginBottom: 12 }}
      />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => handleSave(enabled)} disabled={busy} style={{ padding: '10px 18px' }}>
          {busy ? '저장 중...' : 'URL 저장'}
        </button>
        <button
          className={enabled ? 'btn btn-outline' : 'btn btn-primary'}
          onClick={() => handleSave(!enabled)}
          disabled={busy}
          style={{ padding: '10px 18px' }}
        >
          {enabled ? '✓ 노출 중 — 끄기(OFF)' : '켜기(ON)로 노출'}
        </button>
      </div>
    </div>
  );
}
